/**
 * Letters API Service
 * Handles print-and-mail letter sending via PostGrid/Lob integration
 */

import { apiClient, handleApiError } from './apiClient';

// ============================================================================
// TYPES
// ============================================================================

export interface LetterTemplateCreate {
  template_name: string;
  template_type: 'welcome' | 'utr' | 'closure' | 'notice' | 'custom';
  html_content?: string;
  merge_fields?: string[];
}

export interface LetterTemplate {
  id: number;
  cbo_id: number;
  template_name: string;
  template_type: string;
  html_content?: string;
  merge_fields?: string[];
  is_active: boolean;
  created_at?: string;
}

export interface LetterSendCreate {
  member_id?: number;
  template_id: number;
  recipient_name: string;
  recipient_address_line1: string;
  recipient_address_line2?: string;
  recipient_city: string;
  recipient_state: string;
  recipient_zip: string;
  recipient_address?: string;
  merge_data?: Record<string, string>;
  mail_type?: 'usps_first_class' | 'usps_standard';
  color?: boolean;
  double_sided?: boolean;
  program_id?: number;
  enrollment_id?: number;
  /** TRUS-762: file the letter under this case. */
  case_episode_id?: number;
}

export interface LetterSend {
  id: number;
  cbo_id: number;
  member_id?: number;
  template_id?: number;
  template_name?: string;
  recipient_name?: string;
  recipient_address?: string;
  recipient_address_line1?: string;
  recipient_city?: string;
  recipient_state?: string;
  recipient_zip?: string;
  status: 'created' | 'processing' | 'mailed' | 'delivered' | 'returned' | 'failed';
  external_send_id?: string;
  tracking_url?: string;
  mail_provider?: string;
  mail_type?: string;
  expected_delivery_date?: string;
  error_message?: string;
  sent_at?: string;
  delivered_at?: string;
  returned_at?: string;
  created_at?: string;
}

export interface BulkMailCreate {
  name: string;
  template_id: number;
  group_id?: number;
  program_id?: number;
  member_ids?: number[];
  merge_data_defaults?: Record<string, string>;
  mail_type?: 'usps_first_class' | 'usps_standard';
  color?: boolean;
  double_sided?: boolean;
}

export interface BulkMailCampaign {
  id: number;
  cbo_id: number;
  name: string;
  template_id?: number;
  program_id?: number;
  group_id?: number;
  status: 'draft' | 'queued' | 'processing' | 'completed' | 'cancelled' | 'failed';
  total_count: number;
  sent_count: number;
  failed_count: number;
  delivered_count: number;
  returned_count: number;
  error_message?: string;
  created_at?: string;
  started_at?: string;
  completed_at?: string;
}

export interface ReturnAddress {
  id: number;
  cbo_id: number;
  name: string;
  address_line1: string;
  address_line2?: string;
  city: string;
  state: string;
  zip_code: string;
  is_verified: boolean;
  created_at?: string;
}

export interface ReturnAddressUpsert {
  name: string;
  address_line1: string;
  address_line2?: string;
  city: string;
  state: string;
  zip_code: string;
}

export interface AddressVerifyRequest {
  address_line1: string;
  address_line2?: string;
  city: string;
  state: string;
  zip_code: string;
}

export interface AddressVerifyResult {
  verified: boolean;
  standardized?: string;
  deliverability?: string;
  errors?: string[];
}

// ============================================================================
// TEMPLATE ENDPOINTS
// ============================================================================

export const lettersApi = {
  // Templates
  async createTemplate(data: LetterTemplateCreate): Promise<LetterTemplate> {
    const resp = await apiClient.post('/api/letters/templates', data);
    return resp.data;
  },

  async listTemplates(): Promise<LetterTemplate[]> {
    const resp = await apiClient.get('/api/letters/templates');
    return resp.data;
  },

  async getTemplate(id: number): Promise<LetterTemplate> {
    const resp = await apiClient.get(`/api/letters/templates/${id}`);
    return resp.data;
  },

  async updateTemplate(id: number, data: LetterTemplateCreate): Promise<LetterTemplate> {
    const resp = await apiClient.put(`/api/letters/templates/${id}`, data);
    return resp.data;
  },

  async deleteTemplate(id: number): Promise<void> {
    await apiClient.delete(`/api/letters/templates/${id}`);
  },

  // Individual Sends
  async sendLetter(data: LetterSendCreate): Promise<LetterSend> {
    const resp = await apiClient.post('/api/letters/send', data);
    return resp.data;
  },

  async sendToMember(
    memberId: number,
    templateId: number,
    opts?: { merge_data?: Record<string, string>; mail_type?: string; color?: boolean; program_id?: number }
  ): Promise<LetterSend> {
    const resp = await apiClient.post(`/api/letters/send-to-member/${memberId}`, null, {
      params: { template_id: templateId, ...opts },
    });
    return resp.data;
  },

  // Send Listing & Status
  async listSends(filters?: {
    member_id?: number;
    status?: string;
    program_id?: number;
    enrollment_id?: number;
  }): Promise<LetterSend[]> {
    const resp = await apiClient.get('/api/letters/sends', { params: filters });
    return resp.data;
  },

  async getSend(id: number): Promise<LetterSend> {
    const resp = await apiClient.get(`/api/letters/sends/${id}`);
    return resp.data;
  },

  async refreshSendStatus(id: number): Promise<LetterSend> {
    const resp = await apiClient.post(`/api/letters/sends/${id}/refresh-status`);
    return resp.data;
  },

  async getMemberLetters(memberId: number): Promise<LetterSend[]> {
    const resp = await apiClient.get(`/api/letters/members/${memberId}`);
    return resp.data;
  },

  // Bulk Mail Campaigns
  async createBulkCampaign(data: BulkMailCreate): Promise<BulkMailCampaign> {
    const resp = await apiClient.post('/api/letters/bulk', data);
    return resp.data;
  },

  async listBulkCampaigns(status?: string): Promise<BulkMailCampaign[]> {
    const resp = await apiClient.get('/api/letters/bulk', { params: status ? { status } : {} });
    return resp.data;
  },

  async getBulkCampaign(id: number): Promise<BulkMailCampaign> {
    const resp = await apiClient.get(`/api/letters/bulk/${id}`);
    return resp.data;
  },

  async getBulkCampaignSends(id: number, status?: string): Promise<LetterSend[]> {
    const resp = await apiClient.get(`/api/letters/bulk/${id}/sends`, { params: status ? { status } : {} });
    return resp.data;
  },

  async cancelBulkCampaign(id: number): Promise<BulkMailCampaign> {
    const resp = await apiClient.post(`/api/letters/bulk/${id}/cancel`);
    return resp.data;
  },

  // Return Address
  async upsertReturnAddress(data: ReturnAddressUpsert): Promise<ReturnAddress> {
    const resp = await apiClient.put('/api/letters/return-address', data);
    return resp.data;
  },

  async getReturnAddress(): Promise<ReturnAddress | null> {
    try {
      const resp = await apiClient.get('/api/letters/return-address');
      return resp.data;
    } catch {
      return null;
    }
  },

  // Address Verification
  async verifyAddress(data: AddressVerifyRequest): Promise<AddressVerifyResult> {
    const resp = await apiClient.post('/api/letters/verify-address', data);
    return resp.data;
  },
};

export default lettersApi;
